import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Box,
  Text,
  VStack,
  Select,
  Icon,
  Alert,
  AlertIcon,
  AlertDescription,
  Spinner,
} from '@chakra-ui/react';
import { BrowserMultiFormatReader, NotFoundException } from '@zxing/library';
import { FiCamera } from 'react-icons/fi';
import { parseQRPayload } from 'utils/qrPayload';
import { getModeAccent } from './scannerTheme';

// Visor de cámara a pantalla completa (ocupa el 100% del contenedor padre).
// El marco de escaneo y la línea animada usaban un coral fijo — ahora
// toman el color del tipo de lector activo (getModeAccent, mismo mapeo
// verde/naranjo/azul que el fondo "interactivo" de USBScanner/
// ManualScanner/ScannerShell), para que cámara/USB/manual se sientan un
// solo sistema en vez de pantallas sueltas.
const QRScanner = ({ active = true, onScan, mode }) => {
  const accent = getModeAccent(mode);
  const videoRef   = useRef(null);
  const readerRef  = useRef(null);
  const lastScanRef = useRef('');

  const [devices, setDevices]           = useState([]);
  const [deviceId, setDeviceId]         = useState(null); // null = cámara por defecto
  const [camError, setCamError]         = useState(null);
  const [initializing, setInitializing] = useState(true);

  const overlayBg = 'rgba(0, 0, 0, 0.45)';

  const startDecoding = useCallback((selectedDeviceId) => {
    if (!videoRef.current) return;
    if (readerRef.current) readerRef.current.reset();

    const reader = new BrowserMultiFormatReader();
    readerRef.current = reader;

    // selectedDeviceId puede ser null → usa cámara por defecto
    reader
      .decodeFromVideoDevice(selectedDeviceId ?? null, videoRef.current, (result, err) => {
        // Primer callback = cámara activa; aprovechar para enumerar con permisos ya dados
        if (initializing) {
          setInitializing(false);
          navigator.mediaDevices?.enumerateDevices()
            .then(all => all.filter(d => d.kind === 'videoinput'))
            .then(devs => setDevices(devs))
            .catch(() => {});
        }

        if (!active) return;

        if (result) {
          const raw   = result.getText();
          const token = parseQRPayload(raw);
          if (!token || token === lastScanRef.current) return;
          lastScanRef.current = token;
          setTimeout(() => { lastScanRef.current = ''; }, 3000);
          onScan(token);
        }

        if (err && !(err instanceof NotFoundException)) {
          // NotFoundException es normal entre frames
        }
      })
      .catch((e) => {
        if (/NotAllowed|Permission/i.test(e.message)) {
          setCamError('Permiso de cámara denegado. Habilítalo en la configuración del navegador.');
        } else {
          setCamError('No se pudo iniciar la cámara: ' + e.message);
        }
        setInitializing(false);
      });
  }, [active, onScan, initializing]);

  // Arrancar al montar
  useEffect(() => {
    startDecoding(null);
    return () => { readerRef.current?.reset(); };
  }, []); // eslint-disable-line

  // Cambio de cámara desde el selector
  useEffect(() => {
    if (deviceId !== null) startDecoding(deviceId);
  }, [deviceId]); // eslint-disable-line

  // Pausa/reanuda según prop active
  useEffect(() => {
    if (!readerRef.current) return;
    if (active) {
      lastScanRef.current = '';
      startDecoding(deviceId);
    } else {
      readerRef.current.reset();
    }
  }, [active]); // eslint-disable-line

  if (camError) {
    return (
      <Box position="absolute" inset={0} display="flex" alignItems="center"
        justifyContent="center" p={4} bg="gray.900">
        <Alert status="error" borderRadius="16px" maxW="360px">
          <AlertIcon />
          <AlertDescription>{camError}</AlertDescription>
        </Alert>
      </Box>
    );
  }

  return (
    <Box position="absolute" inset={0} bg="black" overflow="hidden">
      <video
        ref={videoRef}
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        muted
        playsInline
      />

      {/* Selector de cámara (si hay más de una) */}
      {devices.length > 1 && (
        <Select
          position="absolute"
          top={3}
          left={3}
          size="md"
          h="44px"
          fontSize="14.5px"
          maxW="220px"
          value={deviceId ?? ''}
          onChange={(e) => setDeviceId(e.target.value)}
          borderRadius="10px"
          bg="blackAlpha.600"
          color="white"
          borderColor="whiteAlpha.400"
          zIndex={2}
          sx={{ '& option': { color: 'black' } }}
        >
          {devices.map((d) => (
            <option key={d.deviceId} value={d.deviceId}>
              {d.label || `Cámara ${d.deviceId.slice(0, 8)}`}
            </option>
          ))}
        </Select>
      )}

      {/* Overlay oscuro en las esquinas + marco de escaneo */}
      {!initializing && (
        <>
          <Box position="absolute" inset={0}
            bg={overlayBg}
            style={{
              WebkitMaskImage: 'radial-gradient(ellipse 60% 40% at 50% 45%, transparent 100%, black 100%)',
              maskImage:       'radial-gradient(ellipse 60% 40% at 50% 45%, transparent 100%, black 100%)',
            }}
          />
          {/* Marco de escaneo */}
          <Box
            position="absolute"
            top="45%"
            left="50%"
            transform="translate(-50%, -50%)"
            w={{ base: '70%', md: '50%' }}
            maxW="380px"
            style={{ aspectRatio: '1' }}
            border="3px solid"
            borderColor={active ? accent : 'gray.400'}
            borderRadius="20px"
            transition="border-color 0.3s"
          />
          {/* Línea animada de escaneo */}
          {active && (
            <Box
              position="absolute"
              left="15%"
              w={{ base: '70%', md: '50%' }}
              maxW="380px"
              h="3px"
              bg={accent}
              borderRadius="full"
              opacity={0.8}
              animation="scanLine 2s ease-in-out infinite"
              style={{ top: '15%' }}
            />
          )}
        </>
      )}

      {/* Spinner de inicialización */}
      {initializing && (
        <Box
          position="absolute" inset={0}
          display="flex" alignItems="center" justifyContent="center"
          bg="blackAlpha.700"
        >
          <VStack>
            <Spinner color="white" size="xl" />
            <Text color="white" fontSize="17px" fontWeight="600">Iniciando cámara...</Text>
          </VStack>
        </Box>
      )}

      {/* Instrucción */}
      {active && !initializing && (
        <VStack spacing={2} position="absolute" bottom={5} left={0} right={0}>
          <Icon as={FiCamera} color="whiteAlpha.900" boxSize={6} />
          <Text fontSize={{ base: '16px', md: '17px' }} color="whiteAlpha.900" fontWeight="600" textShadow="0 1px 4px rgba(0,0,0,0.6)">
            Apunta la cámara al código QR del carnet
          </Text>
        </VStack>
      )}

      {/* CSS para animación de la línea */}
      <style>{`
        @keyframes scanLine {
          0%   { top: 15%; }
          50%  { top: calc(15% + 70%); }
          100% { top: 15%; }
        }
      `}</style>
    </Box>
  );
};

export default QRScanner;
